import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { getBankCards } from 'services/bankManagements'
import { toman } from 'utilities/helper/format'

/**
 * «چطور دریافت کنید؟» — the part-payment rule and where the money should land.
 *
 * The part-payment question is the one that keeps this feature honest. A fund
 * rarely has the whole amount sitting idle: قرض‌الحسنه funds run their balance
 * down to zero on purpose, because money parked is money not helping anyone.
 * So «می‌توانم بخشی را بگیرم» is usually the difference between getting paid
 * next week and getting paid next month — but not always, and a member saving
 * for one indivisible thing needs to be able to say so.
 *
 * The card is optional. In a صندوق نگران the manager pays members from their
 * own account and only records it here, so demanding an account up front would
 * block a request the fund can actually honour.
 */
export default function StepTerms({
    amount,
    partialAccepted,
    cardId,
    onChange,
}: {
    amount: number
    partialAccepted: boolean
    cardId: number | null
    onChange: (changes: {
        partialAccepted?: boolean
        cardId?: number | null
    }) => void
}) {
    const navigate = useNavigate()
    // No staleTime: the «افزودن حساب بانکی» button below sends the member to
    // /manage-bank-cards, and the whole point of coming back is that the list
    // changed. A 60s cache would show them the same empty list they just left
    // to fix.
    const { data } = useQuery(['bank-cards'], () => getBankCards())
    // guarded: a member with no registered account is the normal case here,
    // and indexing into an empty list is what took the loan wizard's review
    // step down with «باگ خوردیم»
    const cards = data?.results ?? []

    return (
        <div className=" flex flex-col gap-3">
            <div className=" rounded-2xl border border-stroke bg-white p-4">
                <p className=" text-sm font-bold text-heading">
                    اگر صندوق همهٔ مبلغ را نداشت چه؟
                </p>

                <Choice
                    selected={partialAccepted}
                    onSelect={() => onChange({ partialAccepted: true })}
                    title="بخشی از مبلغ را هم می‌گیرم"
                    detail="معمولاً زودتر به پولتان می‌رسید. مدیر می‌تواند مبلغ را در چند نوبت پرداخت کند و شما باقیمانده را می‌بینید."
                />
                <Choice
                    selected={!partialAccepted}
                    onSelect={() => onChange({ partialAccepted: false })}
                    title={`فقط همهٔ ${toman(amount)} را می‌خواهم`}
                    detail="مدیر تا وقتی همهٔ مبلغ آماده نشود نمی‌تواند پرداختی ثبت کند. اگر پول را برای یک هزینهٔ مشخص می‌خواهید این را انتخاب کنید."
                />
            </div>

            <div className=" rounded-2xl border border-stroke bg-white p-4">
                <p className=" text-sm font-bold text-heading">
                    به کدام حساب واریز شود؟
                </p>
                <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                    اختیاری است. در صندوق‌هایی که پول نزد مدیر است، مدیر خودش
                    پرداخت می‌کند و فقط آن را ثبت می‌کند.
                </p>

                <div className=" mt-3 flex flex-col gap-2">
                    {cards.map((card) => (
                        <button
                            key={card.id}
                            type="button"
                            onClick={() =>
                                onChange({
                                    cardId:
                                        cardId === card.id ? null : card.id,
                                })
                            }
                            className={` rounded-xl border p-3 text-right ${
                                cardId === card.id
                                    ? ' border-brand bg-brand-soft'
                                    : ' border-stroke'
                            }`}
                        >
                            <span className=" block text-xs font-bold text-text-body">
                                {card.bank_name}
                            </span>
                            {/* card_number is null on IBAN-only accounts —
                                one of the first real accounts we looked at was
                                a Pasargad row with no card number, which
                                rendered as a bank name over an empty line.
                                Fall back to the IBAN so every row is
                                identifiable. */}
                            <span
                                dir="ltr"
                                className=" mt-[2px] block text-left text-[11px] text-text-secondary"
                            >
                                {card.card_number || card.iban || '—'}
                            </span>
                        </button>
                    ))}

                    <button
                        type="button"
                        onClick={() => navigate('/manage-bank-cards')}
                        className=" rounded-xl border border-dashed border-stroke py-3 text-xs font-bold text-brand"
                    >
                        {cards.length ? 'افزودن حساب دیگر' : 'افزودن حساب بانکی'}
                    </button>
                </div>
            </div>
        </div>
    )
}

function Choice({
    selected,
    onSelect,
    title,
    detail,
}: {
    selected: boolean
    onSelect: () => void
    title: string
    detail: string
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-pressed={selected}
            className={` mt-3 block w-full rounded-xl border p-3 text-right ${
                selected ? ' border-brand bg-brand-soft' : ' border-stroke'
            }`}
        >
            <span className=" block text-xs font-bold text-text-body">
                {title}
            </span>
            <span className=" mt-1 block text-[11px] leading-6 text-text-secondary">
                {detail}
            </span>
        </button>
    )
}
